import React, { useState, useRef } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";

/**
 * 2×2 treemap-style grid for the RUL vs Warranty overview.
 *
 * Layout:
 *   Row 1: data[0]  |  data[1]
 *   Row 2: data[2]  |  data[3]
 *
 * Cells with value === 0 are hidden. Rows where all cells are 0 are hidden.
 * Row heights are proportional to the sum of values in each row.
 * Cell widths within each row are proportional to individual values.
 *
 * Props:
 *   data                      - array of exactly 4 { key, title, color, value }
 *   loading                   - boolean
 *   onCellClick               - (item) => void
 *   selectedConfigName        - string shown in tooltip header
 *   baseEntityTypePluralLable - string (e.g. "Batteries")
 *   height                    - number (default 374)
 */
const RULWarrantyTreemap = ({
  data = [],
  loading,
  onCellClick,
  selectedConfigName,
  baseEntityTypePluralLable = "Batteries",
  height = 374,
}) => {
  const [tooltip, setTooltip] = useState(null); // { item, x, y } — relative to container
  const containerRef = useRef(null);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (data.length < 4) return null;

  const allRows = [
    [data[0], data[1]],
    [data[2], data[3]],
  ];

  // Filter out zero-value cells; filter out rows where all cells are zero
  const rows = allRows
    .map((row) => row.filter((item) => item.value > 0))
    .filter((row) => row.length > 0);

  if (rows.length === 0) {
    return (
      <Box
        sx={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px dashed #D8D8D8",
          borderRadius: "8px",
          backgroundColor: "#FAFAFA",
        }}
      >
        <Typography sx={{ fontSize: "14px", color: "#9E9E9E" }}>
          No {baseEntityTypePluralLable.toLowerCase()} found for this warranty configuration
        </Typography>
      </Box>
    );
  }

  // Each visible cell gets at least 15% of its row so narrow cells remain legible.
  // This works at the layout level and avoids relying on CSS min-width vs flex shrink.
  const ROW_MIN_FRACTION = 0.15;
  const effectiveFlex = (item, rowItems) => {
    const rawTotal = rowItems.reduce((s, i) => s + i.value, 0);
    return Math.max(item.value, rawTotal * ROW_MIN_FRACTION);
  };

  const rowFlex = rows.map((row) =>
    row.reduce((sum, item) => sum + effectiveFlex(item, row), 0)
  );

  const TOOLTIP_WIDTH = 196;
  const TOOLTIP_HEIGHT = 80;

  const handleMouseMove = (e, item) => {
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!containerRect) return;
    const x = e.clientX - containerRect.left + 12;
    const y = e.clientY - containerRect.top + 12;
    setTooltip({
      item,
      x: Math.max(8, Math.min(x, containerRect.width - TOOLTIP_WIDTH)),
      y: Math.max(8, Math.min(y, containerRect.height - TOOLTIP_HEIGHT)),
    });
  };

  const renderCell = (item, row) => (
    <Box
      key={item.key}
      onClick={() => onCellClick?.(item)}
      onMouseMove={(e) => handleMouseMove(e, item)}
      onMouseLeave={() => setTooltip(null)}
      sx={{
        flex: `${effectiveFlex(item, row)} 1 0`,
        position: "relative",
        backgroundColor: item.color,
        borderRadius: "6px",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: 1.5,
        overflow: "hidden",
        opacity: tooltip && tooltip.item.key !== item.key ? 0.45 : 1,
        transition: "opacity 0.2s ease",
      }}
    >
      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: 600,
          color: "#FFFFFF",
          textAlign: "center",
          lineHeight: "20px",
          pointerEvents: "none",
        }}
      >
        {item.title}
      </Typography>
      <Typography
        sx={{
          fontSize: "13px",
          fontWeight: 400,
          color: "rgba(255,255,255,0.9)",
          textAlign: "center",
          lineHeight: "18px",
          mt: 0.5,
          pointerEvents: "none",
        }}
      >
        {item.value} {baseEntityTypePluralLable}
      </Typography>
    </Box>
  );

  return (
    <Box
      ref={containerRef}
      sx={{ position: "relative", display: "flex", flexDirection: "column", gap: "4px", height }}
    >
      {rows.map((row, rowIdx) => (
        <Box
          key={rowIdx}
          sx={{ display: "flex", gap: "4px", flex: `${rowFlex[rowIdx]} 1 0` }}
        >
          {row.map((item) => renderCell(item, row))}
        </Box>
      ))}

      {/* Tooltip — rendered at container level so it is never clipped by a cell's overflow:hidden */}
      {tooltip && (
        <Box
          sx={{
            position: "absolute",
            left: tooltip.x,
            top: tooltip.y,
            width: 180,
            borderRadius: "6px",
            overflow: "hidden",
            border: "1px solid #E3E3E3",
            backgroundColor: "#FFFFFF",
            boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
            zIndex: 10,
            pointerEvents: "none",
          }}
        >
          {selectedConfigName && (
            <Box sx={{ px: "10px", py: "6px", bgcolor: "#EEF0F4" }}>
              <Typography sx={{ fontSize: "13px", color: "#1F1F1F", fontWeight: 700 }}>
                {selectedConfigName}
              </Typography>
            </Box>
          )}
          <Box sx={{ px: "10px", py: "8px", display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: tooltip.item.color, flexShrink: 0 }} />
            <Typography sx={{ fontSize: "12px", color: "#4A4A4A" }}>
              {baseEntityTypePluralLable}:&nbsp;
              <Box component="span" sx={{ fontWeight: 600, color: "#1F1F1F" }}>{tooltip.item.value}</Box>
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default RULWarrantyTreemap;
